import React, {useEffect, useState} from "react";
import {Team, User} from "../../../services/types.ts";
import {useSetAtom} from "jotai/index";
import {errorAtom, teamsGroupStepAtom, teamsGroupUsersAtom} from "../../../store.ts";
import {useGraphQuery} from "../../../services/useGraphQuery.ts";
import {
    Button,
    Checkbox,
    Dropdown,
    makeStyles,
    Option,
    Spinner,
    Table,
    TableBody,
    TableCell,
    TableHeader,
    TableHeaderCell,
    TableRow,
    TableSelectionCell,
    Text,
    Title3,
    tokens
} from "@fluentui/react-components";
import {ArrowLeft24Regular} from "@fluentui/react-icons";
import {useTeamMembersMutation} from "../../../services/useTeamMembersMutation.ts";
import {ConfirmAudienceModal} from "./ConfirmModal.tsx";
import {useAtomValue} from "jotai";
import {useTranslation} from "react-i18next";

const useStyles = makeStyles({
    container: {
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
        padding: '16px',
        height: '100%',
        width: '100%',
        minWidth: '660px', /* Ensures minimum width for all columns to display properly */
        overflowX: 'auto',
    },
    emailCell: {
        whiteSpace: 'normal',
        wordBreak: 'break-word',
        maxWidth: '220px',
    },
    responsiveTable: {
        position: 'relative',
        zIndex: 1,
        '@media (max-width: 640px)': {
            '& table': {
                display: 'block',
                width: '100%',
            },
            '& thead': {
                display: 'none',
            },
            '& tbody': {
                display: 'block',
                width: '100%',
            },
            '& tr': {
                display: 'flex',
                flexDirection: 'column',
                marginBottom: '12px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24)',
                borderRadius: '4px',
                background: tokens.colorNeutralBackground2,
                padding: '12px',
                transition: 'background-color 0.2s ease',
                position: 'relative',
                borderLeft: '4px solid transparent',
            },
            '& tr.selected': {
                background: tokens.colorNeutralBackground1,
                borderLeftColor: tokens.colorBrandBackground,
            },
            '& td': {
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                border: 'none',
                padding: '8px 0',
                width: '100%',
                position: 'relative',
                '&::before': {
                    content: 'attr(data-label)',
                    fontWeight: 'bold',
                    marginRight: '8px',
                    flexShrink: 0,
                },
                '& span, & div': {
                    wordBreak: 'break-word',
                },
            },
        },
    },
    buttonContainer: {
        display: 'flex', 
        gap: '8px', 
        marginTop: '16px',
        '@media (max-width: 640px)': {
            flexDirection: 'column',
            width: '100%'
        }
    },
    primaryButton: {
        marginLeft: 'auto',
        '@media (max-width: 640px)': {
            marginLeft: 0,
            marginTop: '8px'
        }
    },
    formField: {
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
        marginRight: '16px'
    },
    selectedTeams: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: '8px',
        marginTop: '16px',
    },
    teamBadge: {
        display: 'flex',
        alignItems: 'center',
        backgroundColor: tokens.colorNeutralBackground1,
        border: `1px solid ${tokens.colorNeutralStroke1}`,
        borderRadius: '16px',
        padding: '4px 8px',
        gap: '4px',
        margin: '0',
        position: 'relative',
        '& [role="listbox"]': {
            width: 'auto !important',
            minWidth: '100%',
        }
    },
    dismissIcon: {
        cursor: 'pointer',
    },
    comboBox: {
        maxWidth: '400px',
    },
    noTeams: {
        color: tokens.colorNeutralForeground3,
        fontStyle: 'italic',
    },
    actionButtons: {
        display: 'flex',
        gap: '8px',
        marginTop: '24px',
        marginBottom: '16px',
    },
    participantsList: {
        marginTop: '16px',
    },
    participantsHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '12px',
    },
    searchBox: {
        display: 'flex',
        alignItems: 'center',
        maxWidth: '300px',
        width: '100%',
        '@media (max-width: 768px)': {
            maxWidth: '100%',
        }
    },
    participantRow: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '8px 0',
        borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    },
    userInfo: {
        display: 'flex',
        alignItems: 'center',
    },
    userDetails: {
        display: 'flex',
        flexDirection: 'column',
    },
    userEmail: {
        color: tokens.colorNeutralForeground3,
        fontSize: '12px',
        textAlign: 'right',
        width: '100%',
        '@media (max-width: 640px)': {
            textAlign: 'left',
        }
    },
    loadingContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '12px',
        padding: '24px 0',
    },
    tableContainer: {
        width: '100%',
        overflowX: 'visible',
        overflowY: 'visible',
        position: 'relative',
    },
    tableHeaderActions: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        margin: '12px 0',
        flexWrap: 'wrap',
        gap: '12px',
        '@media (max-width: 768px)': {
            flexDirection: 'column',
            alignItems: 'stretch',
        }
    },
    filtersSection: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        flexWrap: 'wrap',
        marginBottom: '8px',
    },
    stepHeader: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        marginBottom: '16px',
    },
    headerContainer: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    backButton: {
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
    },
    teamDropdown: {
        minWidth: '180px',
        position: 'relative',
        zIndex: 100,
    },
    teamDropdownCell: {
        minWidth: '180px',
        maxWidth: '200px',
        overflow: 'visible',
        '@media (max-width: 640px)': {
            // @ts-ignore
            flexDirection: 'column !important',
            alignItems: 'stretch !important',
            marginBottom: '16px',
            maxWidth: '100%',
            '&::before': {
                marginBottom: '8px',
                marginRight: '0 !important',
            },
        }
    },
    mobileSelectAllButton: {
        display: 'none',
        '@media (max-width: 640px)': {
            display: 'block',
        }
    },
    selectedTeamsSummary: {
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        marginBottom: '16px',
        padding: '12px',
        backgroundColor: tokens.colorNeutralBackground2,
        borderRadius: '4px',
    },
    checkboxColumn: {
        width: '30px',
        textAlign: 'center',
    },
    checkbox: {
        margin: '0 auto',
    },
});

export const SelectParticipantsStep: React.FC = () => {
    const styles = useStyles();
    const [users, setUsers] = useState<User[]>([]);
    const [selectedUserIds, setSelectedUserIds] = useState<Set<string>>(new Set());
    const [selectAll, setSelectAll] = useState<boolean>(false);
    const setCurrentStep = useSetAtom(teamsGroupStepAtom)
    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState<boolean>(false);
    const setError = useSetAtom(errorAtom);
    const teamMembersMutation = useTeamMembersMutation();
    const loadedUsers: User[] = useAtomValue(teamsGroupUsersAtom)
    const {t} = useTranslation();

    const {
        data: teams
    } = useGraphQuery<Team[]>('teams')

    useEffect(() => {
        setUsers(loadedUsers);
    }, [loadedUsers]);

    const handleBackToTeamSelection = () => {
        setCurrentStep('selectTeams');
        setError(null);
        setIsConfirmModalOpen(false);
    }

    const handleAssignedTeamChange = (userId: string, teamId: string) => {
        const assignedTeam = teams?.find(team => team.id === teamId)!;
        setUsers(prevParticipants =>
            prevParticipants.map(user =>
                user.id === userId
                    ? {...user, assignedTeam}
                    : user
            )
        );
    };

    const toggleParticipantSelection = (userId: string) => {
        const newSelection = new Set(selectedUserIds);
        if (newSelection.has(userId)) {
            newSelection.delete(userId);
            setSelectAll(false);
        } else {
            newSelection.add(userId);
            // Check if all are now selected
            setSelectAll(newSelection.size === users.length);
        }
        setSelectedUserIds(newSelection);
    };

    const handleSelectAllToggle = () => {
        if (selectAll) {
            setSelectedUserIds(new Set());
        } else {
            const allIds = users.map(p => p.id);
            setSelectedUserIds(new Set(allIds));
        }
        setSelectAll(!selectAll);
    };

    return <>
        <div className={styles.headerContainer}>
            <Title3>{t("text.select-participants")}</Title3>
        </div>

        {teamMembersMutation.isPending ? (
            <div className={styles.loadingContainer}>
                <Spinner size="medium" label={t("text.loading-participants")}/>
            </div>
        ) : users.length === 0 ? (
            <Text>{t("text.no-participants")}</Text>
        ) : (
            <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'visible' }}>
                <div className={`${styles.tableContainer} ${styles.responsiveTable}`}>
                    <Table aria-label="Participants table" size="medium">
                        <TableHeader>
                            <TableRow>
                                <TableHeaderCell
                                    className={styles.checkboxColumn}
                                ><Checkbox
                                    checked={selectAll}
                                    className={styles.checkbox}
                                    onChange={handleSelectAllToggle}
                                /></TableHeaderCell>
                                <TableHeaderCell style={{ width: '20%' }}>{t("table.header.name")}</TableHeaderCell>
                                <TableHeaderCell colSpan={2} style={{ width: '40%' }}>{t("table.header.email")}</TableHeaderCell>
                                <TableHeaderCell style={{ width: '220px' }}>{t("table.header.team")}</TableHeaderCell>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {users.map(user => (
                                <TableRow 
                                    key={user.id} 
                                    className={selectedUserIds.has(user.id) ? 'selected' : ''}
                                >
                                    <TableSelectionCell
                                        className={styles.checkboxColumn}
                                        checked={selectedUserIds.has(user.id)}
                                        onChange={() => toggleParticipantSelection(user.id)}
                                        data-label={t("table.header.select")}
                                    />
                                    <TableCell data-label={t("table.header.name")}>
                                        <div className={styles.userInfo}>
                                            <Text weight="semibold">{user.displayName}</Text>
                                        </div>
                                    </TableCell>
                                    <TableCell data-label={t("table.header.email")} colSpan={2} className={styles.emailCell}>
                                        {user.mail || user.userPrincipalName}
                                    </TableCell>
                                    <TableCell data-label={t("table.header.team")} className={styles.teamDropdownCell}>
                                        <Dropdown
                                            className={styles.teamDropdown}
                                            value={user.assignedTeam?.displayName}
                                            onOptionSelect={(_, data) => handleAssignedTeamChange(user.id, data.optionValue || user.teams[0].id)}
                                            positioning={{ 
                                                position: 'below',
                                                align: 'start',
                                                coverTarget: false
                                            }}
                                        >
                                            {user.teams.map((team) => (
                                                <Option key={team.id} value={team.id}>
                                                    {team.displayName}
                                                </Option>
                                            ))}
                                        </Dropdown>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>
                
                <div style={{display: 'flex', gap: '8px'}}>
                    <div className={styles.buttonContainer}>
                        <Button
                            appearance="subtle"
                            onClick={handleSelectAllToggle}
                            className={styles.mobileSelectAllButton}
                        >
                            {selectAll ? t("button.unselect-all") : t("button.select-all")}
                        </Button>
                        <Button
                            appearance="subtle"
                            icon={<ArrowLeft24Regular/>}
                            onClick={handleBackToTeamSelection}
                            className={styles.backButton}
                        >
                            {t("button.back-to-team-selection")}
                        </Button>
                        <Button
                            className={styles.primaryButton}
                            appearance="primary"
                            disabled={selectedUserIds.size === 0}
                            onClick={() => setIsConfirmModalOpen(true)}
                        >
                            {t("button.create-audience")} ({selectedUserIds.size})
                        </Button>
                    </div>
                </div>
                            </div>
        )}
        {isConfirmModalOpen && (<ConfirmAudienceModal
            selectedParticipants={loadedUsers.filter(participant => selectedUserIds.has(participant.id)) || []}
            closeModal={
                () => setIsConfirmModalOpen(false)
            } postSuccess={
            () => {
                setSelectedUserIds(new Set());
                setSelectAll(false);
            }
        }/>)}
    </>
}